import { useTranslation } from 'react-i18next'

/**
 * This is the interface the translation should have
 * when adding it in Phrase. The convention we're using
 * is as follows.
 *
 * @example
 * ```
 * {
 *   "DeleteFlow": {
 *     "Rejections": {
 *       "<scope>": {
 *         "<rejection_value>": {
 *           "Title": "",
 *           "Description": "",
 *           "Link": "",
 *           "URL": ""
 *         }
 *       }
 *     },
 *     "Warnings": {
 *       "<scope>": {
 *         "<rejection_value>": {
 *           "Title": "",
 *           "Description": "",
 *           "Link": "",
 *           "URL": ""
 *           "ShowMultipleLinks": "" // optional
 *         }
 *       }
 *     }
 *   }
 * }
 * ```
 * The property `ShowMultipleLinks` is optional and used to indicate
 * if we can render links to multiple related items or not.
 *
 * The choice to use this convention was made so that
 * we don't have to keep translation configuration
 * in the code. Using this convention we can just add
 * new translations to support new delete rejections and warnings.
 */
interface IssueTranslation {
  Title: string
  Description: string
  Link: string
  URL: string
  /**
   * Adding this key to the translation will enable
   * rendering multiple links in the issue. It uses
   * i18n to check if the key exists.
   */
  ShowMultipleLinks: '' | undefined
}

export interface UseIssueTranslationOptions {
  kind: 'rejection' | 'warning'
  value: string
  deletedItemString: string
  scope: string
  uuids: string[]
}

interface UseIssueTranslationResult {
  description: string
  link: string
  multiple: boolean
  title: string
  urls: string[]
}

export function useIssueTranslation({
  deletedItemString,
  kind,
  scope,
  uuids,
  value
}: UseIssueTranslationOptions): UseIssueTranslationResult {
  const { t, i18n } = useTranslation()

  function buildIssueKey(kind: UseIssueTranslationOptions['kind'], value: string): string {
    const issueKind = kind === 'rejection' ? 'Rejections' : 'Warnings'

    return `DeleteFlow.${issueKind}.${scope}.${value}`
  }

  const translationKey = buildIssueKey(kind, value)

  const translation = t<string, { returnObjects: true }, IssueTranslation>(translationKey, {
    replace: {
      item: deletedItemString
    },
    returnObjects: true
  })

  function interpolateURLs(key: string, uuids: string[]): string[] {
    return uuids.map(uuid => {
      return t(`${key}.URL`, { replace: { uuid } })
    })
  }

  const multiple = i18n.exists(`${translationKey}.ShowMultipleLinks`)

  return {
    description: translation.Description,
    link: translation.Link,
    multiple,
    title: translation.Title,
    urls: multiple ? interpolateURLs(translationKey, uuids) : [translation.URL].filter(Boolean)
  }
}
